import React, { useEffect, useRef, useState } from "react";
import { Alert } from "@mui/material";
import { ShieldCheck } from "./icons";
import { LockIcon } from "./accessIcons";
import styles from "../publicvehicle.module.css";

const RESEND_SECONDS = 30;
const CODE_LENGTH = 6;

/*
 * "Quick security check" for the guest access page: the code is texted when the card opens, then the guest
 * types it. PhoneVerify's two-step (send, then enter) flow doesn't match this design, so this is the
 * code-only version of it.
 * - sendCode() -> Promise<phoneHint>, verifyCode(code) -> Promise (reject with a message to show).
 * - notice: message shown above the code (e.g. "verify again" after the session ended).
 */
const SecurityCheck = ({ phoneHint, notice, sendCode, verifyCode }) => {
  const [hint, setHint] = useState(phoneHint);
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputs = useRef([]);
  const sentOnce = useRef(false);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const requestCode = async () => {
    setSending(true);
    setError("");
    try {
      const sentTo = await sendCode();
      if (sentTo) setHint(sentTo);
      setDigits(Array(CODE_LENGTH).fill(""));
      setSecondsLeft(RESEND_SECONDS);
      setTimeout(() => inputs.current[0]?.focus(), 50);
    } catch (message) {
      setError(String(message));
    } finally {
      setSending(false);
    }
  };

  // Text the code as soon as the check is shown
  useEffect(() => {
    if (sentOnce.current) return;
    sentOnce.current = true;
    requestCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCode = async (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== CODE_LENGTH) {
      setError("Please enter the 6-digit code.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await verifyCode(code);
    } catch (message) {
      setError(String(message));
      setBusy(false);
    }
  };

  const setDigit = (index, value) => {
    const clean = value.replace(/\D/g, "");
    if (clean.length > 1) {
      // Pasted or autofilled code
      const next = clean.slice(0, CODE_LENGTH).split("");
      setDigits(Array(CODE_LENGTH).fill("").map((d, i) => next[i] || ""));
      inputs.current[Math.min(next.length, CODE_LENGTH) - 1]?.focus();
      return;
    }
    const next = digits.slice();
    next[index] = clean;
    setDigits(next);
    if (clean && index < CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) inputs.current[index - 1]?.focus();
  };

  return (<form className={`${styles.card} ${styles.centerText}`} onSubmit={submitCode} noValidate>
    <div className={styles.iconCircle}><ShieldCheck /></div>
    <h2 className={styles.cardTitle}>Quick security check</h2>
    <p className={styles.cardText}>To make sure it's you opening the gate, enter the one-time code we texted to <strong>{hint || "your phone"}</strong>.</p>
    {notice && <Alert severity="info" className={styles.gaFullWidth}>{notice}</Alert>}
    <div role="group" aria-label="Verification code" className={`${styles.codeGrid} ${styles.gaFullWidth}`}>
      {digits.map((digit, index) => (<input
        key={index}
        ref={(el) => { inputs.current[index] = el; }}
        aria-label={`Digit ${index + 1}`}
        inputMode="numeric"
        autoComplete={index === 0 ? "one-time-code" : "off"}
        maxLength={index === 0 ? CODE_LENGTH : 1}
        value={digit}
        disabled={busy}
        className={digit ? styles.codeFilled : ""}
        onChange={(e) => setDigit(index, e.target.value)}
        onKeyDown={(e) => onKeyDown(index, e)}
      />))}
    </div>
    <div className={styles.codeActions}>
      {sending && <span className={styles.muted}>Sending code...</span>}
      {!sending && secondsLeft > 0 && <span className={styles.muted}>Resend code in 0:{String(secondsLeft).padStart(2, "0")}</span>}
      {!sending && secondsLeft <= 0 && <button type="button" className={styles.linkButton} onClick={requestCode} disabled={busy}>Resend code</button>}
    </div>
    {error !== "" && <Alert severity="error" className={styles.gaFullWidth}>{error}</Alert>}
    <button type="submit" className={`${styles.primaryButton} ${styles.fullWidth}`} disabled={busy || digits.join("").length !== CODE_LENGTH}>{busy ? "VERIFYING..." : "VERIFY"}</button>
    <p className={styles.gaFootnote}><LockIcon size={15} /> You'll stay signed in on this phone until your pass ends</p>
  </form>);
};

export default SecurityCheck;
